/* eslint-env browser */
// Reports › Fleet admin earnings (wave 3, W2_Portal_Tables look): one row per
// fleet admin and month in the modern table, with Export (CSV and print), as
// before (exportButton: true).
//
// Data, exactly as before: the feed (fetchFleetAdminEarnings) is asked for
// once per mount, only when the slice is empty and not loading (deferred
// from AuthLoading for RTDB cost). Nothing is written.
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { DataTable, calmCaps } from '../components/modern';

// The feed reports an empty result as a failure with this text.
const NO_DATA_MSG = 'No data available.';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

// Month names in the portal's language, falling back to the English name.
const monthName = (lang, monthIndex, fallback) => {
  const m = Number(monthIndex);
  if (monthIndex === undefined || monthIndex === null || !Number.isInteger(m) || m < 0 || m > 11) return fallback || '';
  try {
    return new Intl.DateTimeFormat(lang || 'en', { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, m, 15)));
  } catch (e) {
    return fallback || '';
  }
};
const monthIndexOf = (row) => {
  const m = Number(row.month);
  if (row.month !== undefined && row.month !== null && Number.isInteger(m)) return m;
  const i = MONTHS.indexOf(row.monthsName);
  return i >= 0 ? i : 0;
};

export default function FleetAdminEarning() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const columns = [
    { id: 'fleetUId', title: t('fleetadmin_id'), field: 'fleetUId', cellSx: { fontVariantNumeric: 'tabular-nums' } },
    { id: 'fleetadminName', title: t('fleetadmin_name'), field: 'fleetadminName' },
    { id: 'year', title: t('year'), field: 'year' },
    {
      id: 'month',
      title: t('months'),
      field: 'monthsName',
      render: (row) => monthName(lang, row.month, row.monthsName),
      searchValue: (row) => monthName(lang, row.month, row.monthsName),
      exportValue: (row) => monthName(lang, row.month, row.monthsName),
      sortValue: (row) => Number(row.year) * 100 + monthIndexOf(row),
    },
    { id: 'total_rides', title: t('booking_count'), field: 'total_rides', type: 'number' },
    { id: 'fleetCommission', title: t('earning_amount'), field: 'fleetCommission', type: 'money' },
  ];

  const [data, setData] = useState([]);
  const fleetadminearningdata = useSelector((state) => state.fleetadminearningdata);
  const dispatch = useDispatch();
  const { fetchFleetAdminEarnings } = api;
  // Deferred from AuthLoading (RTDB cost): fetch once per mount, and only when
  // the slice is empty and not already loading, so tab-switch remounts never
  // re-run the heavy legacy read.
  const requestedRef = useRef(false);
  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    if (!fleetadminearningdata.fleetadminearning && !fleetadminearningdata.loading && !(fleetadminearningdata.error && fleetadminearningdata.error.flag)) {
      dispatch(fetchFleetAdminEarnings());
    }
  }, [dispatch, fetchFleetAdminEarnings, fleetadminearningdata.fleetadminearning, fleetadminearningdata.loading, fleetadminearningdata.error]);

  useEffect(() => {
    if (fleetadminearningdata.fleetadminearning) {
      setData(fleetadminearningdata.fleetadminearning);
    }
  }, [fleetadminearningdata.fleetadminearning]);

  const loading = !!fleetadminearningdata.loading;
  const error = fleetadminearningdata.error || {};
  const failed = !loading && !!error.flag && error.msg !== NO_DATA_MSG && !data.length;
  const title = calmCaps(t('fleetadmin_earning_reports', { defaultValue: 'Fleet admin earning reports' }));

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <DataTable
        title={title}
        rows={data}
        columns={columns}
        rowKey={(row, index) => `${row.fleetUId || 'f'}-${row.year}-${row.monthsName}-${index}`}
        loading={loading && !data.length}
        refreshing={loading && data.length > 0}
        error={failed ? { title: t('fleet_earnings_load_failed', { defaultValue: 'Could not load the fleet admin earnings.' }) } : null}
        countText={({ shown, total, query, loading: busy }) =>
          busy
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown: fmt(shown), total: fmt(total), defaultValue: '{{shown}} of {{total}}' })
              : t('table_count', { count: total, n: fmt(total), defaultValue_one: '{{n}} row', defaultValue_other: '{{n}} rows', defaultValue: '{{n}} rows' })
        }
        prefsKey="fleetadmin_earnings"
        exportCsv
        exportPrint
        exportFileName="fleetadmin_earnings"
        exportTitle={title}
        minWidth={820}
        emptyState={({ query }) =>
          query || failed
            ? null
            : { icon: <PaymentsOutlinedIcon />, title: t('fleet_earnings_empty', { defaultValue: 'No fleet admin earnings yet' }) }
        }
      />
    </Box>
  );
}
